import {
  AppWindow,
  Bot,
  Building2,
  Cpu,
  Blocks,
  Cloud,
  Gauge,
  HardDrive,
  KeyRound,
  Network,
  Shield,
  SpellCheck,
  UserPlus,
  UsersRound,
  MessagesSquare,
} from '@lucide/vue';
import type { NxCommand } from '@blpde/nx-ui';
import type { CommandFactory } from './commands';
import { appLocationCommand } from './appCommand';

/**
 * The admin's create actions: the list's own „… anlegen“ button, reached with
 * `?new=<value>` (the view opens its dialog through `useRouteIntent`). `section`
 * names the screen for the hint, since two lists both say „Gruppe anlegen“.
 */
const CREATE_ACTIONS: {
  id: string;
  path: string;
  value?: string;
  label: string;
  section: string;
  icon: NxCommand['icon'];
  keywords: string[];
}[] = [
  { id: 'accounts', path: '/admin/accounts', label: 'admin.accounts.create', section: 'admin.nav.accounts', icon: UserPlus, keywords: ['konto', 'account', 'benutzer', 'user'] },
  { id: 'applications', path: '/admin/applications', label: 'admin.applications.create', section: 'admin.nav.applications', icon: AppWindow, keywords: ['app', 'application', 'anwendung'] },
  { id: 'tenants', path: '/admin/tenants', label: 'admin.tenants.create', section: 'admin.nav.tenants', icon: Network, keywords: ['tenant', 'mandant', 'container'] },
  { id: 'companies', path: '/admin/companies', label: 'admin.companies.create', section: 'admin.nav.companies', icon: Building2, keywords: ['firma', 'company', 'mandant'] },
  { id: 'groups', path: '/admin/groups', label: 'admin.groups.create', section: 'admin.nav.groups', icon: UsersRound, keywords: ['gruppe', 'group'] },
  { id: 'permissions', path: '/admin/permissions', value: 'permission', label: 'admin.permissions.actions.createDef', section: 'admin.nav.permissions', icon: Shield, keywords: ['berechtigung', 'permission', 'recht'] },
  { id: 'roles', path: '/admin/permissions', value: 'role', label: 'admin.permissions.actions.createRole', section: 'admin.nav.permissions', icon: Shield, keywords: ['rolle', 'role'] },
  { id: 'grants', path: '/admin/permissions', value: 'grant', label: 'admin.permissions.actions.createGrant', section: 'admin.nav.permissions', icon: Shield, keywords: ['zuteilung', 'grant', 'berechtigung'] },
  { id: 'agents', path: '/admin/agents', label: 'admin.agents.create', section: 'admin.nav.agents', icon: Bot, keywords: ['agent', 'ki', 'ai'] },
  { id: 'providers', path: '/admin/providers', label: 'admin.providers.create', section: 'admin.nav.providers', icon: Cpu, keywords: ['anbieter', 'provider', 'ki', 'ai', 'llm'] },
  { id: 'mcpGroups', path: '/admin/mcp-groups', label: 'admin.mcpGroups.create', section: 'admin.nav.mcpGroups', icon: Blocks, keywords: ['mcp', 'gruppe', 'group', 'tools'] },
  { id: 'driveMounts', path: '/admin/drive', label: 'admin.drive.mounts.create', section: 'admin.drive.nav.mounts', icon: HardDrive, keywords: ['drive', 'mount', 'laufwerk', 'smb'] },
  { id: 'driveStorageConnections', path: '/admin/drive/storage-connections', label: 'admin.drive.storageConnections.create', section: 'admin.drive.nav.storageConnections', icon: HardDrive, keywords: ['drive', 'speicher', 'anbindung', 'storage', 'connection', 'smb', 'objektspeicher'] },
  { id: 'driveShares', path: '/admin/drive/shares', label: 'admin.drive.shares.create', section: 'admin.drive.nav.shares', icon: Cloud, keywords: ['drive', 'share', 'freigabe'] },
  { id: 'oauthClients', path: '/admin/oauth', value: 'client', label: 'admin.oauth.createClient', section: 'admin.nav.oauth', icon: KeyRound, keywords: ['oauth', 'client', 'app'] },
];

/**
 * The admin's palette entries (I-2348): every create action of its lists, the
 * storage limits, the one admin screen without a navbar entry, and Write's
 * company dictionary. Texts come from the admin's own table.
 */
export const adminCommands: CommandFactory = ({ router, t, appLabel: admin }) => [
  ...CREATE_ACTIONS.map((action) =>
    appLocationCommand(router, {
      id: `admin.new.${action.id}`,
      app: 'admin',
      group: admin,
      label: `${admin}: ${t(action.label)}`,
      hint: t(action.section),
      href: `${action.path}?new=${action.value ?? '1'}`,
      icon: action.icon,
      keywords: ['admin', 'anlegen', 'neu', 'new', 'create', ...action.keywords],
    }),
  ),
  appLocationCommand(router, {
    id: 'admin.storage.quotas',
    app: 'admin',
    group: admin,
    label: `${admin}: ${t('admin.storage.quotas.title')}`,
    hint: t('admin.nav.storage'),
    href: '/admin/storage/quotas',
    icon: Gauge,
    category: 'navigation',
    keywords: ['admin', 'storage', 'speicher', 'limits', 'quota', 'kontingent'],
  }),
  // The company and tenant dictionaries (I-3006, I-3024): the settings' dictionary page points here.
  appLocationCommand(router, {
    id: 'admin.companyDictionary',
    app: 'admin',
    group: admin,
    label: `${admin}: ${t('admin.companyDictionary.title')}`,
    hint: t('admin.nav.general'),
    href: '/admin/company-dictionary',
    icon: SpellCheck,
    category: 'navigation',
    keywords: ['admin', 'wörterbuch', 'firmenwörterbuch', 'dictionary', 'company dictionary', 'rechtschreibung', 'spelling', 'spell check', 'synonyme', 'korrekturen', 'suchwörterbuch'],
  }),
  appLocationCommand(router, {
    id: 'admin.tenantDictionary',
    app: 'admin',
    group: admin,
    label: `${admin}: ${t('admin.tenantDictionary.title')}`,
    hint: t('admin.nav.general'),
    href: '/admin/tenant-dictionary',
    icon: SpellCheck,
    category: 'navigation',
    keywords: ['admin', 'wörterbuch', 'mandanten-container-wörterbuch', 'dictionary', 'tenant dictionary', 'rechtschreibung', 'spelling', 'spell check', 'synonyme', 'korrekturen', 'suchwörterbuch'],
  }),
  appLocationCommand(router, {
    id: 'admin.chat',
    app: 'admin',
    group: admin,
    label: `${admin}: ${t('admin.chat.title')}`,
    href: '/admin/chat',
    icon: MessagesSquare,
    category: 'navigation',
    keywords: ['admin', 'chat', 'giphy', 'gif', 'api key', 'dienste', 'integrations'],
  }),
];
